import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/admin';
import { Entity } from '../../../../shared/models/entity';
import { EntityFormData } from '../../../../shared/models/entity-form-data';
import { EntityForm } from '../entity-form/entity-form';
import { Observable, of } from 'rxjs';
import { PermissionService } from '../../../../core/authorization/permission';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'app-entities',
	standalone: true,
	imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatSortModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, FormsModule],
	templateUrl: './entities.html',
	styleUrl: './entities.css'
})
export class Entities implements OnInit, AfterViewInit {
	dataSource       : MatTableDataSource<Entity> = new MatTableDataSource<Entity>([]);
	displayedColumns : string[]     = ['name', 'entityEmail', 'legalRepresentative', 'legalRepresentativeEmail', 'status', 'createdAt', 'actions'];
	canCreateEntity$ : Observable<boolean>;
	canEditEntity$   : Observable<boolean>;
	canDeleteEntity$ : Observable<boolean>;

	@ViewChild(MatSort) sort!: MatSort;
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	constructor(private adminService: AdminService, private dialog: MatDialog, private permissionService: PermissionService) {
		this.canCreateEntity$ = this.permissionService.hasPermission('entities.create');
        this.canEditEntity$   = this.permissionService.hasPermission('entities.update');
        this.canDeleteEntity$ = of(false);

		this.dataSource.filterPredicate = (data: Entity, filter: string) => {
			const searchString = filter.trim().toLowerCase();
			const nameMatch                     = data.name.toLowerCase().includes(searchString);
			const emailMatch                    = data.entityEmail.toLowerCase().includes(searchString);
			const legalRepresentativeMatch      = data.legalRepresentative.toLocaleLowerCase().includes(searchString);
			const legalRepresentativeEmailMatch = data.legalRepresentativeEmail.toLocaleLowerCase().includes(searchString);

			return nameMatch || emailMatch || legalRepresentativeMatch || legalRepresentativeEmailMatch;
		};
	}

	ngOnInit(): void {
		this.loadEntities();
	}

	ngAfterViewInit(): void {
		this.dataSource.sort      = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	async loadEntities(): Promise<void> {
		const result = await this.adminService.getAllEntities();

		if(result) {
			this.dataSource.data = result;
		}
	}

	applyFilter(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();

		if(this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	async onCreateEntity(): Promise<void> {
		const dialogRef = this.dialog.open(EntityForm, {
			width: '600px',
			disableClose: true,
			data: null
		});

		dialogRef.afterClosed().subscribe(async (result: EntityFormData | undefined) => {
			if(result) {
				const success = await this.adminService.createEntity({
					name                     : result.name,
					entityEmail              : result.entityEmail,
					legalRepresentative      : result.legalRepresentative,
					legalRepresentativeEmail : result.legalRepresentativeEmail,
					status                   : result.status
				});

				if(success) {
					this.loadEntities();
				}
			}
		});
	}

	async onEditEntity(entity: Entity): Promise<void> {
		const formData: EntityFormData = {
			id                       : entity.id,
			name                     : entity.name,
			entityEmail              : entity.entityEmail,
			legalRepresentative      : entity.legalRepresentative,
			legalRepresentativeEmail : entity.legalRepresentativeEmail,
			status                   : entity.status
		};

		const dialogRef = this.dialog.open(EntityForm, {
			width        : '600px',
			disableClose : true,
			data         : formData
		});

		dialogRef.afterClosed().subscribe(async (result: EntityFormData | undefined) => {
			if(result) {
				const entityId = result.id;
				
				const updatePayload: Partial<Omit<EntityFormData, 'id'>> = {
					name                    : result.name,
					entityEmail             : result.entityEmail,
					legalRepresentative     : result.legalRepresentative,
					legalRepresentativeEmail: result.legalRepresentativeEmail,
					status                  : result.status
				};
				
				const success = await this.adminService.updateEntity(entityId!, updatePayload);

				if(success) {
					this.loadEntities();
				}
			}
		});
	}

	async onDeleteEntity(id: string): Promise<void> {
		if(window.confirm('¿Estás seguro de que quieres eliminar esta entidad? Esta acción es irreversible.')) {
			const success = await this.adminService.deleteEntity(id);

			if(success) {
				this.loadEntities();
			}
		}
	}
}
